import { useState } from 'react'
import { useToast } from '../../../context/ToastContext.jsx'
import NdrResponseModal from './NdrResponseModal.jsx'
import { fmtDay, fmtStamp } from './trackingUtils'

/**
 * A failed delivery attempt: why, and what the customer can do about it.
 * `onRespond(body)` sends the answer (logged-in order page or a signed link)
 * and returns the API promise; `onResponded` refreshes the tracking data.
 */
export default function NdrResponseCard({ ndr, onRespond, onResponded }) {
  const { showToast } = useToast()
  const [open, setOpen] = useState(false)
  if (!ndr) return null
  const response = ndr.response

  const submit = async (body) => {
    const res = await onRespond(body)
    showToast(res?.message || "Thanks - we've asked the courier to deliver again.")
    onResponded?.()
  }

  return (
    <div role="status" className="rounded-2xl border border-red-200 bg-red-50/60 p-5 normal-case">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex gap-3">
          <i className="fa-solid fa-triangle-exclamation text-red-500 mt-0.5" aria-hidden="true"></i>
          <div>
            <p className="text-sm font-semibold text-primary">
              Delivery attempt {ndr.attempt > 1 ? `#${ndr.attempt} ` : ''}failed
            </p>
            <p className="text-sm text-primary/70 mt-0.5">
              {ndr.reason || 'The courier could not complete the delivery.'}
              {ndr.raised_at && <span className="text-primary/40"> · {fmtStamp(ndr.raised_at)}</span>}
            </p>
            {ndr.can_respond && !onRespond && (
              <p className="text-xs text-primary/50 mt-2">
                To reschedule, use the link in your delivery email or open this order from your account.
              </p>
            )}
            {response && (
              <p className="text-sm text-green-700 mt-2">
                <i className="fa-solid fa-check mr-1.5" aria-hidden="true"></i>
                {response.submitted ? 'Re-delivery requested' : 'Request received - our team is arranging it'}
                {response.preferred_date && ` for ${fmtDay(response.preferred_date)}`}.
              </p>
            )}
          </div>
        </div>
        {ndr.can_respond && onRespond && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="h-11 px-5 rounded-xl bg-primary text-white text-xs font-bold tracking-[0.15em] uppercase hover:bg-secondary shrink-0"
          >
            Reschedule delivery
          </button>
        )}
      </div>
      <NdrResponseModal open={open} onClose={() => setOpen(false)} onSubmit={submit} />
    </div>
  )
}
